import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../../slices/authSlice';

const DEVICES = [
    { id: 'desktop', label: 'Desktop', width: 1280 },
    { id: 'tablet', label: 'Tablet', width: 768 },
    { id: 'mobile', label: 'Mobile', width: 375 },
];

const Preview = () => {
    const token = useSelector((s) => s.auth.token);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [projects, setProjects] = useState([]);
    const [activeProject, setActiveProject] = useState(null);
    const [project, setProject] = useState(null);
    const [device, setDevice] = useState('desktop');
    const [showSource, setShowSource] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!token) return;
        axios
            .get('/api/projects?summary=true', { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => {
                const { createdProjects = [], sharedProjects = [] } = r.data || {};
                const unique = new Map([...createdProjects, ...sharedProjects].map((p) => [p._id, p]));
                const list = [...unique.values()].filter((p) => !p.archivedAt);
                setProjects(list);
                if (list.length) setActiveProject(list[0]._id);
                setLoading(false);
            })
            .catch((err) => {
                if (err.response && err.response.status === 401) {
                    dispatch(logout());
                    navigate('/login');
                } else {
                    setError('Failed to load projects.');
                    setLoading(false);
                }
            });
    }, [token, dispatch, navigate]);

    useEffect(() => {
        if (!activeProject || !token) return;
        axios
            .get(`/api/projects/${activeProject}`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => setProject(r.data.project))
            .catch(() => setError('Failed to load preview.'));
    }, [activeProject, token]);

    const segments = useMemo(() => {
        if (!project) return [];
        return (project.sections || []).map((src, i) => ({
            i,
            src,
            tgt: (project.translatedSections || [])[i] || '',
        }));
    }, [project]);

    const deviceWidth = DEVICES.find((d) => d.id === device).width;

    if (loading) return <div style={{ padding: 40, color: 'var(--ink-3)' }}>Loading preview…</div>;
    if (error) return <div style={{ padding: 40, color: 'crimson' }}>{error}</div>;

    return (
        <>
            <div className="rs-page-title-row">
                <h1 className="rs-page-title">In-context preview</h1>
                <div className="rs-page-actions">
                    <select
                        className="set-input"
                        value={activeProject || ''}
                        onChange={(e) => setActiveProject(e.target.value)}
                        style={{ height: 36 }}
                    >
                        {projects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
                    </select>
                </div>
            </div>
            <p className="rs-page-sub">See translations rendered the way your audience will read them.</p>

            <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: 'var(--bg-card)', border: '1px solid var(--line)',
                borderRadius: 12, padding: 12, marginBottom: 16,
            }}>
                <div className="bv-seg">
                    {DEVICES.map((d) => (
                        <button
                            key={d.id}
                            type="button"
                            className={device === d.id ? 'on' : ''}
                            onClick={() => setDevice(d.id)}
                        >
                            {d.label}
                        </button>
                    ))}
                </div>
                <span style={{ flex: 1 }} />
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--ink-3)' }}>
                    <input type="checkbox" checked={showSource} onChange={(e) => setShowSource(e.target.checked)} />
                    Show source alongside
                </label>
            </div>

            <div style={{
                background: 'var(--bg-soft)', borderRadius: 16, padding: 20,
                display: 'flex', justifyContent: 'center', minHeight: 500,
            }}>
                <div style={{
                    width: `min(${deviceWidth}px, 100%)`,
                    background: 'var(--bg-card)',
                    borderRadius: 12, padding: '32px 40px',
                    boxShadow: 'var(--shadow-md)',
                    transition: 'width 0.2s',
                }}>
                    {project && (
                        <>
                            <h2 style={{ marginTop: 0, fontSize: 20, letterSpacing: '-0.02em' }}>
                                {project.name}
                            </h2>
                            <p style={{ color: 'var(--ink-3)', fontSize: 12, marginTop: -4 }}>
                                Preview · {(project.targetLanguage || '').toUpperCase()}
                            </p>
                            <hr style={{ border: 0, borderTop: '1px solid var(--line)', margin: '12px 0 20px' }} />
                            {segments.length === 0 && (
                                <p style={{ color: 'var(--ink-4)', fontStyle: 'italic' }}>
                                    This project has no segments yet.
                                </p>
                            )}
                            {segments.map((s) => (
                                <div key={s.i} style={{ marginBottom: 16 }}>
                                    {showSource && (
                                        <p style={{
                                            fontSize: 11,
                                            color: 'var(--ink-4)',
                                            margin: 0,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.04em',
                                        }}>
                                            {(project.sourceLanguage || 'EN').toUpperCase()}: {s.src}
                                        </p>
                                    )}
                                    <p style={{ fontSize: 14, lineHeight: 1.6, margin: showSource ? '4px 0 0' : 0 }}>
                                        {s.tgt || <span style={{ color: 'var(--ink-4)', fontStyle: 'italic' }}>— Not translated —</span>}
                                    </p>
                                </div>
                            ))}
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default Preview;
